import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Paper, Button, CircularProgress, Grid, Card, CardContent, Alert, FormControl, InputLabel, Select, MenuItem, Link as MuiLink } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService, { ModelConfig, Dataset } from '../services/api';
import Header from '../components/Header';
import { RENAISSANCE_COLORS } from '../utils';

interface WpEvalRunResult {
  status: string;
  eval_runid?: string;
  rows_processed?: number;
  output_path?: string;
  s3_parquet_path?: string;
  s3_qwk_path?: string;
  qwk_scores?: {
    [key: string]: number;
  };
  qwk_file?: string;
  timestamp?: string;
  error?: string;
}

const WpEvalRun: React.FC = () => {
  const navigate = useNavigate();
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<WpEvalRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [batchSize, setBatchSize] = useState(20);
  const [randomSample, setRandomSample] = useState(true);
  const [maxSamples, setMaxSamples] = useState<number | undefined>(undefined);
  const [modelConfig, setModelConfig] = useState<ModelConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [selectedDataset, setSelectedDataset] = useState<string | undefined>(undefined);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loadingDatasets, setLoadingDatasets] = useState(false);

  useEffect(() => {
    const fetchModelConfig = async () => {
      try {
        setLoadingConfig(true);
        const config = await apiService.getModelConfig();
        if (config && config.status === 'success') {
          setModelConfig(config);
        } else {
          console.error('Invalid model config response:', config);
          toast.error('Invalid model configuration response');
        }
      } catch (err: any) {
        console.error('Failed to load model configuration:', err);
        const errorMessage = err?.response?.data?.error || err?.message || 'Failed to load model configuration';
        console.error('Error details:', {
          message: err?.message,
          response: err?.response?.data,
          status: err?.response?.status
        });
        toast.error(errorMessage);
      } finally {
        setLoadingConfig(false);
      }
    };
    fetchModelConfig();
  }, []);

  useEffect(() => {
    const loadDatasets = async () => {
      try {
        setLoadingDatasets(true);
        const response = await apiService.listDatasets('parquet'); // Only show parquet files (ready to use)
        if (response.status === 'success') {
          setDatasets(response.datasets || []);
        } else {
          console.error('Failed to load datasets:', response);
          setDatasets([]);
        }
      } catch (err: any) {
        console.error('Failed to load datasets:', err);
        setDatasets([]);
        // Don't show error toast - datasets are optional
      } finally {
        // Always reset loading state
        setLoadingDatasets(false);
      }
    };
    loadDatasets();
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleRunEvaluation = async () => {
    setError(null);
    setResult(null);
    try {
      setIsRunning(true);

      // Pass dataset key directly - backend will use S3_DATASETS_BUCKET from environment
      const datasetS3Path = selectedDataset;

      // Use new async API - creates a job and returns immediately
      const response = await apiService.createEvaluation({
        batch_size: batchSize,
        random_sample: randomSample,
        output_prefix: "eval_results",
        dataset_s3_path: datasetS3Path,
        max_samples: maxSamples || undefined
      });

      if (response.status !== 'success') {
        setError((response as any)?.error || 'Failed to create evaluation job.');
        toast.error((response as any)?.error || 'Failed to create evaluation job.');
      } else {
        toast.success('Evaluation job created! Redirecting to job details...');
        // Navigate to job detail page to watch progress
        navigate(`/evaluations/${response.job_id}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create evaluation job.');
      toast.error(err.message || 'Failed to create evaluation job.');
      setIsRunning(false);
    }
  };

  const handleDatasetChange = (event: any) => {
    const value = event.target.value;
    setSelectedDataset(value === '' ? undefined : value);
  };

  return (
    <div style={{
      backgroundColor: RENAISSANCE_COLORS.beige,
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    }}>
      <Header />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main' }}>
            Run Evaluation
          </Typography>

          <Typography variant="body1" paragraph>
            Run a complete evaluation including batch scoring and QWK analysis. This will process multiple responses
            and calculate the Quadratic Weighted Kappa (QWK) scores for each trait.
          </Typography>

          <Box sx={{ mb: 4 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="dataset-select-label">Select Dataset (Optional)</InputLabel>
              <Select
                labelId="dataset-select-label"
                id="dataset-select"
                value={selectedDataset || ''}
                label="Select Dataset (Optional)"
                onChange={handleDatasetChange}
                disabled={loadingDatasets || isRunning}
              >
                <MenuItem value="">
                  <em>Use default dataset</em>
                </MenuItem>
                {datasets.map((dataset) => (
                  <MenuItem key={dataset.key} value={dataset.key}>
                    {dataset.filename} ({dataset.type.toUpperCase()}, {formatFileSize(dataset.size)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {datasets.length === 0 && !loadingDatasets && (
              <Alert severity="info" sx={{ mt: 1 }}>
                No datasets available. <MuiLink component={Link} to="/datasets">Upload a dataset</MuiLink> to use custom data.
              </Alert>
            )}
            {selectedDataset && (
              <Alert severity="success" sx={{ mt: 1 }}>
                Selected dataset: {selectedDataset.split('/').pop()}
              </Alert>
            )}
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Configuration
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Batch Size
                    </Typography>
                    <input
                      type="number"
                      value={batchSize}
                      onChange={(e) => setBatchSize(Number(e.target.value))}
                      min="1"
                      max="100"
                      style={{
                        width: '15%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: `1px solid ${RENAISSANCE_COLORS.lightgrey}`,
                      }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Random Sample
                    </Typography>
                    <input
                      type="checkbox"
                      checked={randomSample}
                      onChange={(e) => setRandomSample(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    <Typography variant="body2" component="span">
                      Use random sampling
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Maximum Samples (Optional)
                    </Typography>
                    <input
                      type="number"
                      value={maxSamples || ''}
                      onChange={(e) => setMaxSamples(e.target.value ? Number(e.target.value) : undefined)}
                      min="1"
                      placeholder="Leave empty to process all rows"
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: `1px solid ${RENAISSANCE_COLORS.lightgrey}`,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: RENAISSANCE_COLORS.mediumgrey, display: 'block', mt: 0.5 }}>
                      Limit the number of samples to test. Useful for large datasets.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Model Configuration
                  </Typography>
                  {loadingConfig ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Loading configuration...</Typography>
                    </Box>
                  ) : modelConfig ? (
                    <>
                      <Typography variant="body2" paragraph>
                        <strong>Model:</strong> {modelConfig.model.name}
                      </Typography>
                      <Typography variant="body2" paragraph>
                        <strong>Temperature:</strong> {modelConfig.model.temperature}
                      </Typography>
                      <Typography variant="body2" paragraph>
                        <strong>Max Tokens:</strong> {modelConfig.model.max_tokens}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Region:</strong> {modelConfig.model.region}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="error">
                      Failed to load model configuration
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleRunEvaluation}
              disabled={isRunning}
              startIcon={isRunning ? <CircularProgress size={20} /> : null}
            >
              {isRunning ? 'Starting Evaluation...' : 'Start Evaluation'}
            </Button>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
        )}

        {result && result.status === 'success' && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
              Evaluation Results
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Summary
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Evaluation ID:</strong> {result.eval_runid}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Rows Processed:</strong> {result.rows_processed}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Output Path:</strong> {result.output_path}
                    </Typography>
                    {result.s3_parquet_path && (
                      <Typography variant="body1" paragraph>
                        <strong>S3 Parquet Path:</strong> {result.s3_parquet_path}
                      </Typography>
                    )}
                    {result.s3_qwk_path && (
                      <Typography variant="body1" paragraph>
                        <strong>S3 QWK File Path:</strong> {result.s3_qwk_path}
                      </Typography>
                    )}
                    <Typography variant="body1">
                      <strong>Timestamp:</strong> {result.timestamp ? new Date(result.timestamp).toLocaleString() : ''}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      QWK Scores
                    </Typography>
                    {result.qwk_scores && typeof result.qwk_scores === 'object' && !Array.isArray(result.qwk_scores) ? (
                      Object.entries(result.qwk_scores).map(([trait, score]) => (
                        <Box key={trait} sx={{ mb: 2 }}>
                          <Typography variant="subtitle1" gutterBottom>
                            {trait}
                          </Typography>
                          <Typography variant="h4" color="primary">
                            {typeof score === 'number' ? score.toFixed(4) : 'N/A'}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography color="error">No QWK scores available or invalid format.</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Alert severity="success">
                Evaluation completed successfully! Results have been saved to S3.
              </Alert>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  component={Link}
                  to="/results"
                  color="primary"
                >
                  View All Results
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
      </Container>
    </div>
  );
};

export default WpEvalRun; 